import cx from 'classnames';
import {map, range} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {Icon} from 'components';

type Props = {
    initialIndex?: number;
    className?: string;
    children: React.ReactNode[];
    onUpdate?: (index: number) => void;
};

export const Carousel: React.FC<Props> = ({initialIndex = 0, children, className, onUpdate}) => {
    const [index, setIndex] = useState(initialIndex);
    const itemsCount = useMemo(() => children.length, [children]);
    const prevDisabled = useMemo(() => index < 1, [index]);
    const nextDisabled = useMemo(() => index >= itemsCount - 1, [index, itemsCount]);
    const indexes = useMemo(() => range(itemsCount), [itemsCount]);

    useEffect(() => {
        onUpdate?.(index);
    }, [index, onUpdate]);

    return (
        <div className={cx('tw-relative', className)}>
            <div className='tw-flex tw-items-center'>
                <div className='tw-min-w-12 tw-shrink-0'>
                    <Icon
                        name='chevron-left'
                        button
                        size='lg'
                        disabled={prevDisabled}
                        onClick={() => setIndex((prev) => prev - 1)}
                    />
                </div>
                <div className='tw-flex-grow'>{children[index]}</div>
                <div className='tw-min-w-12 tw-shrink-0'>
                    <Icon
                        name='chevron-right'
                        button
                        size='lg'
                        disabled={nextDisabled}
                        onClick={() => setIndex((prev) => prev + 1)}
                    />
                </div>
            </div>
            <div className='tw-flex tw-justify-center tw-mt-4'>
                {map(indexes, (i) => (
                    <div
                        key={i}
                        className={cx('tw-w-4 tw-h-1 tw-mx-2 tw-rounded tw-cursor-pointer',
                            {
                                'tw-bg-default': index !== i,
                                'tw-bg-primary': index === i
                            }
                        )}
                        onClick={() => setIndex(i)}
                    />
                ))}
            </div>
        </div>
    );
};